import { Badge, Image } from '@shopify/polaris';
import { SettingUrls } from 'constants/SettingsUrls';
import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import useProviderLimitExceeded from 'hooks/plan/useProviderLimitExceeded';
import ErrorBanner from 'layouts/error-banner/component/ErrorBanner';
import { SubscriptionStatus } from 'models/SubscriptionStatus';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { transformToCdnUrl } from 'utils/google-storage-util';
import { AppStatus } from '../../../../../../../pb/settings/v1/app_status_pb';
import { SettingsWrapper } from '../settings-wrapper/SettingsWrapper';
import { useAppStatusSWR } from './hooks/useAppStatusSWR';
import { updateAppStatus } from './mutations/app-status-mutations';

const AppStatusContent = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { currentSubscription } = useCurrentSubscriptionHelper();
    const { isLimitExceeded, limit } = useProviderLimitExceeded();

    const [saveLoading, setSaveLoading] = useState(false);
    const [error, setError] = useState<boolean>(false);

    const { data, isLoading, error: appStatusError, mutate } = useAppStatusSWR();

    const onActivate = () => {
        // More carriers are active than the plan allows, the merchant has to pick which to keep first
        if (isLimitExceeded) return;
        if (!currentSubscription || currentSubscription.status === SubscriptionStatus.CANCELLED) {
            navigate(SettingUrls.billing);
        } else {
            setSaveLoading(true);
            setError(false);
            updateAppStatus(true)
                .then(() => {
                    mutate();
                })
                .catch((error) => {
                    setError(true);
                })
                .finally(() => {
                    setSaveLoading(false);
                });
        }
    };
    const onDeactivate = () => {
        updateAppStatus(false).then(() => {
            mutate();
        });
    };

    const statusElements = useMemo(() => {
        let badge = <Badge>{t('DASHBOARD.APP_STATUS.INACTIVE')}</Badge>;
        let image = transformToCdnUrl('embedded-assets/dashboard-images/app_status_inactive.webp');
        let subtitle = t('DASHBOARD.APP_STATUS.SUBTITLE_DEACTIVATED');
        const activateButton = (
            <s-button onClick={onActivate} loading={saveLoading} disabled={isLimitExceeded}>
                {t('BUTTON.ACTIVATE')}
            </s-button>
        );
        // Same explanation as ProviderLimitExceededBanner, so the disabled button is not a dead end
        let btn = isLimitExceeded ? (
            <div className="flex items-center gap-1">
                {activateButton}
                <s-icon type="info" interestFor="app-status-activate-tooltip" />
                <s-tooltip id="app-status-activate-tooltip">
                    {t('PROVIDER_LIMIT_EXCEEDED_BANNER.DESCRIPTION', { count: limit })}
                </s-tooltip>
            </div>
        ) : (
            activateButton
        );
        if (data?.appStatus === AppStatus.ACTIVE) {
            badge = <Badge tone={'success'}>{t('DASHBOARD.APP_STATUS.ACTIVE')}</Badge>;
            image = transformToCdnUrl('embedded-assets/dashboard-images/app_status_active.webp');
            subtitle = t('DASHBOARD.APP_STATUS.SUBTITLE_ACTIVATED');
            btn = (
                <s-button onClick={onDeactivate} loading={saveLoading}>
                    {t('BUTTON.DEACTIVATE')}
                </s-button>
            );
        }

        return { badge, image, subtitle, btn };
    }, [data?.appStatus, isLoading, isLimitExceeded, limit, t, onActivate, onDeactivate]);

    return (
        <>
            <SettingsWrapper
                title={t('DASHBOARD.APP_STATUS.APP_STATUS')}
                badge={statusElements.badge}
                description={statusElements.subtitle}
                actions={statusElements.btn}
                image={
                    <Image
                        style={{ width: '50px', height: '50px' }}
                        source={statusElements.image}
                        alt="plan icon"
                        width="50px"
                        height="50px"
                    />
                }
            />
            {error && (
                <ErrorBanner
                    banner={{
                        titleKey: 'DASHBOARD.APP_STATUS.ERROR.TITLE',
                        tone: 'critical',
                        descriptionKey: 'DASHBOARD.APP_STATUS.ERROR.DESCRIPTION',
                        withSupportActions: true,
                    }}
                />
            )}
        </>
    );
};

export default AppStatusContent;
